import { Icon } from './Icon'
import type { ChannelConfig } from '../types'
import styles from './ChannelPill.module.css'

interface ChannelPillProps {
  channel: ChannelConfig
}

export function ChannelPill({ channel }: ChannelPillProps) {
  const active = !channel.paused

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.cardHeader}>
        <div className={styles.nameRow}>
          <div className={`${styles.dot} ${active ? styles.dotActive : styles.dotPaused}`} />
          <span className={styles.name}>{channel.channelName}</span>
        </div>
        <Icon name="chevron_right" size="sm" className={styles.arrow} />
      </div>

      {/* Teams / prefix row */}
      {(channel.prefix || (channel.githubTeamSlugs?.length ?? 0) > 0) && (
        <div className={styles.route}>
          {channel.prefix && <code>{channel.prefix}</code>}
          {channel.githubTeamSlugs?.slice(0, 2).map(s => (
            <code key={s}>{s}</code>
          ))}
          {(channel.githubTeamSlugs?.length ?? 0) > 2 && (
            <span className={styles.moreTeams}>+{(channel.githubTeamSlugs?.length ?? 0) - 2} more</span>
          )}
        </div>
      )}

      {/* Feature pills */}
      <div className={styles.pills}>
        {active ? (
          <span className={`${styles.pill} ${styles.pillActive}`}>
            <Icon name="check_circle" size={13} /> Active
          </span>
        ) : (
          <span className={`${styles.pill} ${styles.pillPaused}`}>
            <Icon name="pause_circle" size={13} /> Paused
          </span>
        )}
        {channel.nudgeEnabled && (
          <span className={`${styles.pill} ${styles.pillNudge}`}>
            <Icon name="schedule" size={13} /> Nudges · {channel.nudgeThresholdDays}d
          </span>
        )}
        {channel.escalationEnabled && (
          <span className={`${styles.pill} ${styles.pillEscl}`}>
            <Icon name="escalator_warning" size={13} /> Escalation
          </span>
        )}
        {channel.weeklyDigestEnabled && (
          <span className={`${styles.pill} ${styles.pillDigest}`}>
            <Icon name="bar_chart" size={13} /> Channel digest
          </span>
        )}
        {channel.personalDigestEnabled && (
          <span className={`${styles.pill} ${styles.pillPersonal}`}>
            <Icon name="person" size={13} /> Personal digest
          </span>
        )}
        {channel.reviewerTaggingEnabled && (
          <span className={`${styles.pill} ${styles.pillTag}`}>
            <Icon name="alternate_email" size={13} /> Tag reviewers
          </span>
        )}
        {channel.dmReviewersEnabled && (
          <span className={`${styles.pill} ${styles.pillDm}`}>
            <Icon name="forward_to_inbox" size={13} /> DM reviewers
          </span>
        )}
        {(channel.authorTeamSlugs?.length ?? 0) > 0 || (channel.watchedAuthorLogins?.length ?? 0) > 0 ? (
          <span className={`${styles.pill} ${styles.pillAuthor}`}>
            <Icon name="visibility" size={13} /> Author watch
          </span>
        ) : null}
        {channel.workingHoursStart && channel.workingHoursEnd && (
          <span className={`${styles.pill} ${styles.pillHours}`}>
            <Icon name="access_time" size={13} />
            {channel.workingHoursStart}–{channel.workingHoursEnd}
            {channel.timezone ? ` (${channel.timezone.split('/').pop()})` : ''}
          </span>
        )}
      </div>
    </div>
  )
}
